import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useClassName } from '@travpro/hooks';
import i18n from 'i18n-react';
import { Multiselect } from 'multiselect-react-dropdown';

const DirectoryFilters = ({ data, onChange, defaultFilters }) => {
  const [selectData, setSelectData] = useState(defaultFilters);
  const [search, setSearch] = useState('');

  const c = useClassName('directoryFilters');
  const t = (key) => i18n.translate(['directoryFilters', key].join('.')); // NOTE: Rudimentary implementation of useTranslate

  const searchDelay = useRef();
  const dropdownRefs = useRef({});
  const inputRefs = useRef({});

  const clearFilters = useCallback(() => {
    Object.values(dropdownRefs.current).forEach((dropdown) => {
      dropdown.resetSelectedValues();
    });

    setSearch('');
    setSelectData({});
  }, []);

  const onSearchInput = useCallback(({ target } = {}) => setSearch(target.value), []);

  const onCheckInput = useCallback(({ target }) => {
    setSelectData((prev) => ({
      ...prev,
      [target.name]: !prev[target.name] || undefined,
    }));
  }, []);

  const onUpdate = useCallback((list, item) => {
    const value = list.map(({ optionId }) => optionId);

    setSelectData((prev) => ({
      ...prev,
      [item.id]: value.length > 0 ? value : undefined,
    }));
  }, []);

  useEffect(() => {
    if (searchDelay.current !== undefined) clearTimeout(searchDelay.current);

    searchDelay.current = setTimeout(() => {
      setSelectData((prev) => ({ ...prev, search: search.length > 0 ? `%${search}%` : undefined }));
      searchDelay.current = undefined;
    }, 500);
  }, [search]);

  useEffect(() => onChange((prev) => ({ ...prev, ...selectData })), [selectData, onChange]);

  return (
    <div {...c()}>
      {data.map(({ id, options, type, props = {} }) => (
        <React.Fragment key={id}>
          {type === 'search' && (
            <input
              ref={(ref) => {
                inputRefs.current.search = ref;
              }}
              {...c('search')}
              value={search}
              placeholder={t(id)}
              type="text"
              onChange={onSearchInput}
            />
          )}
          {(type === 'dropdown' || (type === undefined && options !== undefined)) && (
            <Multiselect
              ref={(ref) => {
                dropdownRefs.current[id] = ref;
              }}
              placeholder={t(id)}
              options={options}
              onSelect={onUpdate}
              onRemove={onUpdate}
              displayValue="name"
              showArrow
              avoidHighlightFirstOption
              {...props}
            />
          )}
          {type === 'check' && (
            <div {...c('checks')}>
              <span>{t('additional')}</span>
              {options.map(({ id: option }) => (
                <div {...c('checkbox')} key={option}>
                  <label htmlFor={option}>
                    <span>{t(option)}</span>

                    <input
                      id={option}
                      name={option}
                      type="checkbox"
                      checked={selectData[option] !== undefined}
                      onChange={onCheckInput}
                      ref={(ref) => {
                        inputRefs.current[option] = ref;
                      }}
                    />

                    <span {...c('checkmark')} id={option} />
                  </label>
                </div>
              ))}
            </div>
          )}
        </React.Fragment>
      ))}
      <button {...c('reset')} type="button" onClick={clearFilters}>
        {t('reset')}
      </button>
    </div>
  );
};

export default DirectoryFilters;

DirectoryFilters.propTypes = {
  defaultFilters: PropTypes.shape(),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      options: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape()), PropTypes.arrayOf(PropTypes.string)]),
      type: PropTypes.string,
      props: PropTypes.shape(),
    }),
  ).isRequired,
  onChange: PropTypes.func,
};

DirectoryFilters.defaultProps = {
  defaultFilters: {
    limit: 50,
    offset: 0,
    orderBy: 'asc',
  },
  onChange: () => undefined,
};
